import { useEffect, useMemo } from 'react';
import {
    getSortOptionKey,
    useSortSearchParamsStateOnly,
} from 'utils/hooks/useSort';
import {
    useFetchInstagramRelatedAffinityRoots,
    useFetchTikTokAffinityRoots,
} from 'utils/hooks/api';
import {
    INSTAGRAM_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from 'utils/metrics';
import { DATA_SOURCES } from '@/config/constants';
import { healthStatFilterOptions } from 'utils/filters';
import { sourceOf } from 'utils/dataSources';
import { useFilterSearchParamsStateOnly } from 'utils/hooks/useFilter';
import useHandleSearchInputChange from 'utils/hooks/useHandleSearchInputChange';
import { useLocation } from 'react-router';
import useTableColumns from 'utils/hooks/useTableColumns';
import useURLSearchParams from 'utils/hooks/useURLSearchParams';
import useStaleFilterNotice from 'utils/hooks/useStaleFilterNotice';

/** Second tuple element is the column's default visibility. */
const BY_SOURCE = {
    [DATA_SOURCES.TIKTOK]: {
        useAffinityRootsHook: useFetchTikTokAffinityRoots,
        metrics: [
            [TIKTOK_METADATA_BY_KEY.non_cum_avg_weekly_views, true],
            [TIKTOK_METADATA_BY_KEY.views, true],
            [TIKTOK_METADATA_BY_KEY.non_cum_increase_MoM_rolling, false],
            [TIKTOK_METADATA_BY_KEY.non_cum_MoM_rolling, false],
            [TIKTOK_METADATA_BY_KEY.non_cum_increase_WoW_rolling, false],
            [TIKTOK_METADATA_BY_KEY.non_cum_WoW_rolling, true],
            [TIKTOK_METADATA_BY_KEY.non_cum_increase_QoQ_rolling, false],
            [TIKTOK_METADATA_BY_KEY.non_cum_QoQ_rolling, true],
            [TIKTOK_METADATA_BY_KEY.non_cum_increase_YoY_rolling, false],
            [TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling, false],
        ],
    },
    [DATA_SOURCES.INSTAGRAM]: {
        useAffinityRootsHook: useFetchInstagramRelatedAffinityRoots,
        metrics: [
            [INSTAGRAM_METADATA_BY_KEY.non_cum_avg_weekly_likes, true],
            [
                INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_MoM_rolling,
                false,
            ],
            [INSTAGRAM_METADATA_BY_KEY.non_cum_likes_MoM_rolling, false],
            [
                INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_WoW_rolling,
                false,
            ],
            [INSTAGRAM_METADATA_BY_KEY.non_cum_likes_WoW_rolling, true],
            [
                INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_QoQ_rolling,
                false,
            ],
            [INSTAGRAM_METADATA_BY_KEY.non_cum_likes_QoQ_rolling, true],
            [
                INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_YoY_rolling,
                false,
            ],
            [INSTAGRAM_METADATA_BY_KEY.non_cum_likes_YoY_rolling, false],
        ],
    },
};

/**
 * Presentation-layer hook for the Related Categories / Related Brands tables.
 * Parameterized over the data-fetching hook so the same plumbing serves
 * cat→related_category, brand→related_category, and cat→related_brand views.
 *
 * `source` selects a hook, so callers must keep it constant per mounted table.
 */
function useRelatedAggregateTableProps({
    source,
    useDataHook,
    affinityGroups,
    brands,
    categories,
    countries,
    week,
    year,
    handleFilter,
    initialSortOption,
    initialFilterOption,
    name,
    getColumnsConfig,
    tableName,
    filterParamSeparator = ',',
    filterParamPrefix,
    sortParamKey = 'sort',
    supportsAffinityGroupFilter = true,
}) {
    const { useAffinityRootsHook, metrics } = BY_SOURCE[source];
    const { parentNameFilterKey } = sourceOf(source);

    const searchParams = useURLSearchParams();
    const initialFilterKey = useMemo(
        () => JSON.stringify(initialFilterOption || {}),
        [initialFilterOption],
    );
    const stableInitialFilterOption = useMemo(
        () => initialFilterOption,
        [initialFilterKey],
    );
    const supportedFiltersKey = Object.keys(handleFilter || {})
        .sort()
        .join(',');
    const supportedFilters = useMemo(
        () => (supportedFiltersKey ? supportedFiltersKey.split(',') : []),
        [supportedFiltersKey],
    );
    const filterParamsSignature = useMemo(
        () => searchParams.toString(),
        [searchParams],
    );

    const groupQuery = useAffinityRootsHook({
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
        week,
        year,
    });
    const fallbackGroupOptions = useMemo(() => {
        const paramKey = filterParamPrefix
            ? `${filterParamPrefix}${parentNameFilterKey}`
            : parentNameFilterKey;
        const paramValue = searchParams.get(paramKey);
        return paramValue
            ? paramValue
                  .split(filterParamSeparator)
                  .map((value) => value.trim())
                  .filter(Boolean)
                  .map((value) => ({ label: value, value }))
            : [];
    }, [
        filterParamPrefix,
        filterParamSeparator,
        parentNameFilterKey,
        searchParams,
    ]);
    const initialGroupOptions = useMemo(() => {
        const initialGroups =
            stableInitialFilterOption?.[parentNameFilterKey] || [];
        return initialGroups.map((option) => ({
            label: option.label ?? option.value,
            value: option.value ?? option.label,
        }));
    }, [parentNameFilterKey, stableInitialFilterOption]);
    const groupOptions = useMemo(
        () =>
            (groupQuery.data || []).map(({ name, id, volume }) => ({
                label: name,
                value: id,
                volume,
            })),
        [groupQuery.data],
    );
    const healthStatOptions = useMemo(
        () => healthStatFilterOptions.map(({ options }) => options).flat(),
        [],
    );
    const filterAccessorsAndOptions = useMemo(() => {
        const optionsByValue = new Map(
            groupOptions.map((option) => [option.value, option]),
        );
        initialGroupOptions.forEach((option) => {
            if (!optionsByValue.has(option.value)) {
                optionsByValue.set(option.value, option);
            }
        });
        fallbackGroupOptions.forEach((option) => {
            if (!optionsByValue.has(option.value)) {
                optionsByValue.set(option.value, option);
            }
        });
        return {
            healthStats: {
                accessor: (option) => option.value,
                options: healthStatOptions,
            },
            [parentNameFilterKey]: {
                accessor: (option) => option.value,
                options: optionsByValue.size
                    ? Array.from(optionsByValue.values())
                    : initialGroupOptions.length
                      ? initialGroupOptions
                      : fallbackGroupOptions,
            },
        };
    }, [
        fallbackGroupOptions,
        groupOptions,
        healthStatOptions,
        initialGroupOptions,
        parentNameFilterKey,
    ]);
    const urlFilterState = useFilterSearchParamsStateOnly({
        filterAccessorsAndOptions,
        initialFilterOption: stableInitialFilterOption,
        supportedFilters: Object.keys(handleFilter || {}),
        separator: filterParamSeparator,
        paramPrefix: filterParamPrefix,
    });
    const { filters, filtersKey, setFilter, setFilters, resetFilters } =
        urlFilterState;
    useStaleFilterNotice(urlFilterState.staleFilters);
    const nameFilter =
        filters && filters.name && filters.name.length ? filters.name[0] : null;

    const handleSearchInputChange = useHandleSearchInputChange({
        nameFilter,
        setFilter,
    });
    const initialSortKey = useMemo(
        () => JSON.stringify(initialSortOption || {}),
        [initialSortOption],
    );
    const stableInitialSortOption = useMemo(
        () => initialSortOption,
        [initialSortKey],
    );
    const sortOptions = useMemo(
        () =>
            metrics.flatMap(([metric]) => [
                metric.getSortOption({ ascending: true }),
                metric.getSortOption({ ascending: false }),
            ]),
        [metrics],
    );
    const {
        setSortOption: baseSetSortOption,
        sortOption,
        sortOptionKey,
    } = useSortSearchParamsStateOnly({
        initialSortOption: stableInitialSortOption,
        options: sortOptions,
        sortOptionKeyAccessor: getSortOptionKey,
        paramKey: sortParamKey,
    });

    const queryData = {
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
        week,
        year,
        enabled: !!(week && year),
    };
    if (supportsAffinityGroupFilter && affinityGroups) {
        queryData.affinity_group = affinityGroups;
    }
    if (
        supportsAffinityGroupFilter &&
        affinityGroups &&
        filters?.[parentNameFilterKey]?.length
    ) {
        throw new Error(
            `Cannot use both affinityGroups and ${parentNameFilterKey} filters`,
        );
    }
    const queryDataFilterAndSortConfig = {
        order_by: sortOption?.sortBy,
        ascending: sortOption?.ascending,
        ...(supportsAffinityGroupFilter &&
            filters?.[parentNameFilterKey]?.length && {
                affinity_group: filters[parentNameFilterKey].map(
                    ({ value }) => value,
                ),
            }),
        ...(filters?.name?.length && { search: filters.name[0] }),
        ...(filters?.healthStats?.filter(
            ({ metricLabel }) => metricLabel === 'price point',
        )?.length && {
            price_segmentation: filters?.healthStats
                ?.filter(
                    ({ metricLabel, label }) =>
                        metricLabel === 'price point' &&
                        ['High', 'Medium', 'Low'].includes(label),
                )
                .map(({ label }) => {
                    if (label === 'High') return 'HIGH PRICE';
                    if (label === 'Medium') return 'MEDIUM PRICE';
                    if (label === 'Low') return 'LOW PRICE';
                    return null;
                }),
        }),
    };

    const { query, getQueryData } = useDataHook({
        ...queryDataFilterAndSortConfig,
        ...queryData,
    });
    const {
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        isFetching,
        ...result
    } = query;
    const rows = (result.data?.pages || []).reduce((acc, { data }) => {
        if (!data) return acc;
        acc.push(...data.map((o) => ({ ...o, relatedName: name })));
        return acc;
    }, []);

    const isSuccess = result.isSuccess;
    const isLoading = result.isLoading || result.isFetchingNextPage;
    const dataReady = isSuccess && rows.length > 0;

    const { visibleOrderedMetrics, columnsMenu, setSortOption } =
        useTableColumns({
            tableName,
            metrics,
            setSortOption: baseSetSortOption,
            sortOption,
        });

    const visibleColumnsConfig = getColumnsConfig(visibleOrderedMetrics);

    const isNoData = !(rows && rows.length);
    let noDataMessage;
    if (!isNoData && isSuccess) {
        noDataMessage = `No data to display. ${
            filters && Object.keys(filters).length
                ? 'Try adjusting the filters'
                : ''
        }`;
    } else if (isNoData && isLoading) {
        noDataMessage = 'Loading...';
    } else if (isNoData && result.isError) {
        noDataMessage =
            'Could not load the data. Refresh the page to try again.';
    } else if (isNoData) {
        noDataMessage = 'No related entities for this view.';
    }

    const { pathname } = useLocation();
    useEffect(() => {
        const hasFilterParams = supportedFilters.some((key) =>
            searchParams.get(
                filterParamPrefix ? `${filterParamPrefix}${key}` : key,
            ),
        );
        const hasInitialFilters =
            Object.keys(stableInitialFilterOption || {}).length > 0;
        if (!hasFilterParams && hasInitialFilters) {
            resetFilters();
        }
    }, [
        pathname,
        resetFilters,
        filterParamsSignature,
        supportedFiltersKey,
        stableInitialFilterOption,
        filterParamPrefix,
    ]);

    return {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData: rows,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        resetFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        pages: result.data?.pages,
        getQueryData,
    };
}

export default useRelatedAggregateTableProps;
